import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
//import { Storage } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { User } from '../models/user';
import { Coupon } from '../models/coupon';
import { environment } from '../../environments/environment';
import { Globals } from '../common/globals';
import { Observable, firstValueFrom } from 'rxjs';
import { BiometricService } from './biometric.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(
    private http: HttpClient,
    private biometricService: BiometricService
  ) { }

  async login(email: string, password: string) : Promise<Observable<User>> {
    var body = { 
      email: email, 
      password: password,
      prepaid: true
    };
    return this.http.post<User>(environment.apiUrl + '/login', JSON.stringify(body), environment.JSON_HEADERS);
  }

  async logout() {
    // Clear biometric credentials
    await this.biometricService.disableBiometric();

    //clear user on local device
    await Preferences.remove({ key: 'currentUser' });
    await Preferences.remove({ key: 'activeCouponId' });
    await Preferences.remove({ key: 'savedCredentials' });

    //clear instance reference
    Globals.currentUser = null;
    Globals.activeCoupon = null;
  }

  deleteUser(email: string, password: string): Observable<any> {
    var vm = { email: email, password: password };
    return this.http.delete(environment.apiUrl + '/users', {
      headers: environment.JSON_HEADERS.headers,
      body: JSON.stringify(vm)
    });
  }

  /**
   * Store credentials locally for auto-login (non-biometric users)
   */
  async storeCredentials(email: string, password: string): Promise<void> {
    await Preferences.set({
      key: 'savedCredentials',
      value: JSON.stringify({ email, password })
    });
  }

  /**
   * Get stored credentials for auto-login
   */
  async getStoredCredentials(): Promise<{ email: string; password: string } | null> {
    const ret = await Preferences.get({ key: 'savedCredentials' });
    if (ret && ret.value) {
      try {
        return JSON.parse(ret.value);
      } catch (e) {
        console.error('Failed to parse stored credentials:', e);
        await Preferences.remove({ key: 'savedCredentials' });
        return null;
      }
    }
    return null;
  }

  /**
   * Store user without password for security
   */
  async storeUserWithoutPassword(user: User): Promise<void> {
    // Create a copy without the password
    const userToStore = { ...user };
    delete userToStore.password;

    await Preferences.set({
      key: 'currentUser',
      value: JSON.stringify(userToStore)
    });

    // In-memory reference can keep password temporarily for session refresh
    Globals.currentUser = user;
  }

  /**
   * Login using biometric authentication
   */
  async loginWithBiometric(): Promise<User | null> {
    const credentials = await this.biometricService.authenticateAndGetCredentials();
    if (!credentials) {
      return null;
    }

    try {
      const loginObservable = await this.login(credentials.email, credentials.password);
      const user = await firstValueFrom(loginObservable);

      // Keep password in memory for session refresh
      user.password = credentials.password;
      Globals.currentUser = user;

      // Store user without password
      await this.storeUserWithoutPassword(user);

      // Load active coupon
      await this.loadActiveCoupon(user);

      return user;
    } catch (error) {
      console.error('Biometric login API call failed:', error);
      return null;
    }
  }

  async getCurrentUser(): Promise<User> {
    var user: User = null;

    if (Globals.currentUser) {
      user = Globals.currentUser;
    } else {
      const ret = await Preferences.get({ key: 'currentUser' });
      if (ret && ret.value) {
        try {
          user = JSON.parse(ret.value);
        } catch (e) {
          console.error('Failed to parse stored user data:', e);
          await Preferences.remove({ key: 'currentUser' });
          user = null;
        }
      }
    }

    //Trim CC number to last 4
    if (user && user.cardNumber && user.cardNumber.length > 4) {
      user.cardNumber = user.cardNumber.substr(user.cardNumber.length - 4, 4);
    }

    return user;
  }
  // load(locationId) {
  //   return this.http.get<Contract[]>(environment.apiUrl + '/contracts?locationId=' + locationId, { headers: { 'Content-Type': 'application/json', 'Authorization': this.authService.key, 'Username': this.authService.currentUsername() } });
  // }

  // loadContract(contractId) {
  //   return this.http.get<Contract>(environment.apiUrl + '/contracts/' + contractId, { headers: { 'Content-Type': 'application/json', 'Authorization': this.authService.key, 'Username': this.authService.currentUsername() } });
  // }

  async createUser(user: User) : Promise<User> {
    let returnUser = await this.http.post<User>(environment.apiUrl + '/users', JSON.stringify(user), { headers: { 'Content-Type': 'application/json' } }).toPromise();

    //user.id = returnUser.id;

    //update qr code
    this.setQrcode(returnUser);

    //persist user on local device
    Preferences.set({
      key: 'currentUser', 
      value: JSON.stringify(returnUser)
    });

    //save instant reference
    Globals.currentUser = returnUser;

    return returnUser;
  }

  async updateUser(user: User) : Promise<User> {
    let returnUser = await this.http.put<User>(environment.apiUrl + '/users', JSON.stringify(user), { headers: { 'Content-Type': 'application/json' } }).toPromise();

    //user.id = returnUser.id;

    //update qr code
    this.setQrcode(returnUser);

    //persist user on local device
    Preferences.set({
      key: 'currentUser', 
      value: JSON.stringify(returnUser)
    });

    //save instant reference
    Globals.currentUser = returnUser;
    console.log('UPDATED USER!!!!!!!!!!!!!!!!!!!!!!');
    console.log(returnUser);
    return returnUser;
  }

  // update(contract) {
  //   return this.http.put<Contract>(environment.apiUrl + `/contracts/${contract.id}`, JSON.stringify(contract), { headers: { 'Content-Type': 'application/json', 'Authorization': this.authService.key, 'Username': this.authService.currentUsername() } });
  // }

  // delete(contract) {
  //   return this.http.delete(environment.apiUrl + `/contracts/${contract.id}`, { headers: { 'Content-Type': 'application/json', 'Authorization': this.authService.key, 'Username': this.authService.currentUsername() } });
  // }

  setQrcode(user: User) {
    var s = user.userId;
    if (user.members) {
      for(var i = 0; i < user.members.length; i++) {
        var member = user.members[i];
        s += ',' + member.locationName + ':' + member.localMemberNumber
      }
    }
    // Use active coupon if set, otherwise no coupon in QR
    if (Globals.activeCoupon) {
      s += ',CPN:' + Globals.activeCoupon.code;
    }
    user.qrcode = s;
  }

  /**
   * Get the default coupon based on priority (filtered by current location):
   * 1. Reservation with earliest start date
   * 2. Credit with earliest start date
   * 3. First coupon in the list
   */
  getDefaultCoupon(user: User): Coupon {
    if (!user?.coupons || user.coupons.length === 0) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get current location context
    const currentLocationId = user.preferredLocationId;

    // Filter coupons by current location context
    const locationCoupons = user.coupons.filter((c) => {
      return c.issueLocationId === currentLocationId;
    });

    if (locationCoupons.length === 0) {
      return null;
    }

    // Separate coupons into categories
    const reservations = locationCoupons.filter((c) => {
      return c.reserveEndDate && new Date(c.reserveEndDate) >= today;
    });
    const credits = locationCoupons.filter((c) => {
      return c.reserveEndDate && new Date(c.reserveEndDate) < today;
    });
    const coupons = locationCoupons.filter((c) => {
      return c.reserveEndDate == null;
    });

    // Priority 1: Reservation with earliest start date
    if (reservations.length > 0) {
      reservations.sort((a, b) => {
        return new Date(a.reserveStartDate).getTime() - new Date(b.reserveStartDate).getTime();
      });
      return reservations[0];
    }

    // Priority 2: Credit with earliest start date
    if (credits.length > 0) {
      credits.sort((a, b) => {
        return new Date(a.reserveStartDate).getTime() - new Date(b.reserveStartDate).getTime();
      });
      return credits[0];
    }

    // Priority 3: First coupon in the list
    if (coupons.length > 0) {
      return coupons[0];
    }

    return null;
  }

  /**
   * Set the active coupon and persist to storage
   */
  async setActiveCoupon(coupon: Coupon) {
    Globals.activeCoupon = coupon;

    if (coupon) {
      await Preferences.set({
        key: 'activeCouponId',
        value: coupon.id.toString()
      });
    } else {
      await Preferences.remove({ key: 'activeCouponId' });
    }

    // Regenerate QR code with new active coupon
    if (Globals.currentUser) {
      this.setQrcode(Globals.currentUser);
    }
  }

  /**
   * Load the active coupon after login/refresh
   * @param user The user with fresh coupon data
   * @param preserveSelection If true, try to keep the current selection if it still exists
   */
  async loadActiveCoupon(user: User, preserveSelection: boolean = false) {
    // If preserving selection and there's a current active coupon
    if (preserveSelection && Globals.activeCoupon) {
      // Try to find the current selection in the fresh data
      const currentCouponId = Globals.activeCoupon.id;
      const foundCoupon = user.coupons?.find(c => c.id === currentCouponId);

      if (foundCoupon) {
        // Keep the existing selection but update with fresh data
        await this.setActiveCoupon(foundCoupon);
        return;
      }
    }

    // Fall back to smart default
    const defaultCoupon = this.getDefaultCoupon(user);
    await this.setActiveCoupon(defaultCoupon);
  }

  /**
   * Get a formatted description of the active coupon
   */
  getActiveCouponDescription(): string {
    const coupon = Globals.activeCoupon;
    if (!coupon) {
      return 'No coupon selected';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Determine coupon type
    let type = 'Coupon';
    if (coupon.reserveEndDate) {
      const endDate = new Date(coupon.reserveEndDate);
      if (endDate >= today) {
        type = 'Reservation';
      } else {
        type = 'Credit';
      }
    }

    // Format date range if available
    let dateRange = '';
    if (coupon.reserveStartDate && coupon.reserveEndDate) {
      const startDate = new Date(coupon.reserveStartDate);
      const endDate = new Date(coupon.reserveEndDate);

      if (coupon.prepaidTotalAmount) {
        // Include time for prepaid reservations: m/d/yyyy h:mm AM/PM
        const dateTimeOptions: Intl.DateTimeFormatOptions = { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' };
        dateRange = `${startDate.toLocaleDateString('en-US', dateTimeOptions)} - ${endDate.toLocaleDateString('en-US', dateTimeOptions)}`;
      } else {
        // Date only for non-prepaid: m/d/yyyy
        const dateOptions: Intl.DateTimeFormatOptions = { month: 'numeric', day: 'numeric', year: 'numeric' };
        dateRange = `${startDate.toLocaleDateString('en-US', dateOptions)} - ${endDate.toLocaleDateString('en-US', dateOptions)}`;
      }
    }

    // Build description with line breaks
    let description = `${type}`;

    // Add code if available
    if (coupon.code) {
      description += ` #${coupon.code}`;
    }

    // Add coupon type description on new line if available
    if (coupon.couponTypeLocationDescription) {
      description += `\n${coupon.couponTypeLocationDescription}`;
    }

    // Add prepaid amount on new line if available
    if (coupon.prepaidTotalAmount) {
      const formattedAmount = coupon.prepaidTotalAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      description += `\nPrepaid Amount: ${formattedAmount}`;
    }

    // Add date range on new line if available
    if (dateRange) {
      description += `\n${dateRange}`;
    }

    return description;
  }
}
